import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { LayoutGrid } from 'lucide-react';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { DEFAULT_ENTITY_KINDS, ENTITY_REGISTRY } from './entity-registry';
import { matchNav } from './nav-match';
import type {
  OmniEntityKind,
  OmniNavSection,
  SearchColumn,
  SearchHit,
} from './types';
import { ENTITY_DEBOUNCE_MS, GROUP_LIMIT } from './types';

type UseOmniSearchResultsArgs = {
  open: boolean;
  query: string;
  sections: OmniNavSection[];
  entities?: OmniEntityKind[];
  canIncludeEntity?: (kind: OmniEntityKind) => boolean;
  onNavigate: (path: string) => void;
};

type EntityCache = Partial<Record<OmniEntityKind, unknown[]>>;

const EMPTY_ROWS: EntityCache = {};

export type OmniSearchResults = {
  columns: SearchColumn[];
  /** True while debounce or entity fetch is still in flight for a non-empty query. */
  loading: boolean;
};

export function useOmniSearchResults({
  open,
  query,
  sections,
  entities = DEFAULT_ENTITY_KINDS,
  canIncludeEntity,
  onNavigate,
}: UseOmniSearchResultsArgs): OmniSearchResults {
  const debounced = useDebouncedValue(query, ENTITY_DEBOUNCE_MS);
  const trimmedQuery = query.trim();
  const trimmedDebounced = debounced.trim();
  // Entity rows and the search they answer, so "fetching" is whether they match the current one.
  const [result, setResult] = useState<{
    q: string;
    kinds: string;
    rows: EntityCache;
  } | null>(null);
  const prevRef = useRef<EntityCache>({});
  const navigateRef = useRef(onNavigate);
  useLayoutEffect(() => {
    navigateRef.current = onNavigate;
  });
  // Only apply results from the latest in-flight search.
  const requestSeqRef = useRef(0);

  const enabledKinds = useMemo(
    () =>
      entities.filter((kind) => {
        if (canIncludeEntity && !canIncludeEntity(kind)) return false;
        return true;
      }),
    [entities, canIncludeEntity]
  );

  const enabledKey = enabledKinds.join(',');

  // An emptied search drops the old rows, so they don't show again under the next query.
  if (open && !trimmedDebounced && result !== null) setResult(null);
  const entityRows = result?.rows ?? EMPTY_ROWS;
  const fetching =
    Boolean(trimmedDebounced) &&
    enabledKey.length > 0 &&
    (result?.q !== trimmedDebounced || result.kinds !== enabledKey);

  // Fetch only when the debounced query changes — not on every keystroke.
  useEffect(() => {
    if (!open) return;

    if (!trimmedDebounced) {
      requestSeqRef.current += 1;
      prevRef.current = {};
      return;
    }

    if (enabledKey.length === 0) return;

    const seq = ++requestSeqRef.current;
    const kinds = enabledKey.split(',') as OmniEntityKind[];
    const q = trimmedDebounced;

    void (async () => {
      const entries = await Promise.all(
        kinds.map(async (kind) => {
          try {
            const rows = await ENTITY_REGISTRY[kind].search(q, GROUP_LIMIT);
            return [kind, rows] as const;
          } catch {
            // Keep previous rows for this kind on network blips.
            return [kind, prevRef.current[kind] ?? []] as const;
          }
        })
      );

      if (seq !== requestSeqRef.current) return;

      const next: EntityCache = {};
      for (const entry of entries) {
        next[entry[0]] = entry[1];
      }
      prevRef.current = next;
      setResult({ q, kinds: enabledKey, rows: next });
    })();
  }, [open, trimmedDebounced, enabledKey]);

  const columns = useMemo(() => {
    const next: SearchColumn[] = [];
    const go = (path: string) => navigateRef.current(path);

    const pageItems = matchNav(sections, query, GROUP_LIMIT);
    if (pageItems.length > 0) {
      const items: SearchHit[] = pageItems.map((item) => {
        const Icon = item.icon;
        return {
          key: `page-${item.key}`,
          label: item.label,
          node: (
            <div className="flex min-w-0 items-center gap-2">
              {Icon ? (
                <Icon className="size-3.5 shrink-0 text-muted-foreground" />
              ) : null}
              <div className="min-w-0">
                <div className="truncate font-medium">{item.label}</div>
                {item.hint ? (
                  <div className="truncate text-xs text-muted-foreground">
                    {item.hint}
                  </div>
                ) : null}
              </div>
            </div>
          ),
          onSelect: () => go(item.path),
        };
      });
      next.push({
        id: 'pages',
        heading: 'Pages',
        icon: LayoutGrid,
        items,
      });
    }

    // Hide entity columns as soon as the live query is empty (not waiting on debounce).
    if (trimmedQuery) {
      for (const kind of enabledKinds) {
        const source = ENTITY_REGISTRY[kind];
        const rows = entityRows[kind] ?? [];
        if (rows.length === 0) continue;
        next.push({
          id: kind,
          heading: source.heading,
          icon: source.icon,
          items: rows.map((row) => ({
            key: `${kind}-${source.getId(row)}`,
            label: source.getLabel(row),
            node: source.renderOption(row),
            onSelect: () => go(source.href(row)),
          })),
        });
      }
    }

    return next;
  }, [sections, query, trimmedQuery, enabledKinds, entityRows]);

  const waitingOnEntities =
    open &&
    trimmedQuery.length > 0 &&
    enabledKinds.length > 0 &&
    (trimmedQuery !== trimmedDebounced || fetching);

  return { columns, loading: waitingOnEntities };
}
